import React, { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, Route, Routes, useLocation, useNavigationType } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { Header } from 'app/features/header';
import { navigationService } from 'app/service/navigation/navigation-service';
import { AuthCallback, CheckAuth, Logout, ProvAuthCallback } from 'app/features/auth';
import { ContextProvider } from 'app/features/context-provider';
import { useScrollToTop } from 'app/hooks';
import { GtmNoscript } from 'app/components/gtm';
import { GlobalFatalErrorBoundary } from 'app/components/error';
import { config } from './config/config';
import { Footer } from './features/footer';
import { ToastMessage } from './features/toast-message/toast-message';
import { handleSentryBeforeSend } from './utils/sentry-utils';
import { TranslatorBar } from './components/translator-bar/translator-bar';
import { CustomerSegmentPicker } from './pages/customer-segment-picker/customer-segment-picker';

if (config.sentry.enabled === 'true') {
  const { REACT_APP_SENTRY_RELEASE } = process.env;
  Sentry.init({
    beforeSend: handleSentryBeforeSend,
    dsn: config.sentry.dsn,
    ignoreErrors: [
      // Session timeouts
      /Authorization response state ([0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}) doesn't match request state ([0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$)/,

      // Operational messages might fail, useless for us.
      /\/operationalmessages\/customer\/\d+$/,

      // Consent rejection
      /^Third.+script.+consent:$/,

      // Browser specific errors
      /chrome-extension:\/\//,
      /@webkit-masked-url\(:\/\/hidden\/\)/,
    ],
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: Number.parseFloat(config.sentry.tracingRate) || 0.05,
    release: REACT_APP_SENTRY_RELEASE,
    environment: config.sentry.environment,
    replaysOnErrorSampleRate: 1,
    replaysSessionSampleRate: 0,
    normalizeDepth: 5,
  });
}
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const App: React.FC = () => {
  useScrollToTop();

  return (
    <SentryRoutes>
      <Route path={navigationService.PAGES.logout.url} element={<Logout />} />
      <Route
        path="/*"
        element={
          <GlobalFatalErrorBoundary>
            <GtmNoscript />
            <div data-testid="app">
              <ToastMessage />
              <Header />
              <TranslatorBar />
              <SentryRoutes>
                <Route path={navigationService.PAGES.authCallback.url} element={<AuthCallback />} />
                <Route path={navigationService.PAGES.provAuthCallback.url} element={<ProvAuthCallback />} />
                <Route
                  path={navigationService.PAGES.segmentPicker.url}
                  element={<CustomerSegmentPicker variant="all" />}
                />
                <Route
                  index={true}
                  path="/*"
                  element={
                    <CheckAuth>
                      <ContextProvider />
                    </CheckAuth>
                  }
                />
              </SentryRoutes>
              <Footer />
            </div>
          </GlobalFatalErrorBoundary>
        }
      />
    </SentryRoutes>
  );
};
